/**
 * ErrorMessageBanner: BR-U6-5準拠のエラー表示（業務エラー／システムエラーの区別表示）。
 * NFR Design 1.1: 自動リトライなし・手動再試行導線を提供する。
 * design-mockups/login-prompt-and-error-banner.html準拠:
 * 業務エラー=黄系背景+⚠️アイコン、システムエラー=グレー系背景+🛑アイコン。
 */

import { createElement, createShadowHost, applyStylesheet } from "../core/shadowDomUtil";
import { BACKDROP_MODAL, SHADOW_MODAL } from "./designTokens";
import type { ErrorKind } from "../types";

/** 常時単一インスタンスとするためのShadow HostのコンテナID（既存があれば破棄してから再生成する）。 */
const BANNER_HOST_ID = "spica-error-message-banner";

const BANNER_STYLES = `
:host { all: initial; }
.spica-error-overlay { position: fixed; inset: 0; z-index: 2147483647; display: flex; align-items: center; justify-content: center; padding: 24px; background: ${BACKDROP_MODAL}; }
.spica-error-banner { display: flex; align-items: flex-start; gap: 10px; border-radius: 8px; padding: 20px 22px; font-family: sans-serif; font-size: 14px; box-shadow: ${SHADOW_MODAL}; max-width: 420px; }
.spica-error-banner.business { background: #fff8e6; border: 1px solid #f6c94c; color: #6b5300; }
.spica-error-banner.system { background: #f5f5f5; border: 1px solid #ccc; color: #555; }
.spica-error-banner-icon { font-size: 16px; }
.spica-error-message { margin: 0; flex: 1; }
.spica-error-message-link { color: inherit; text-decoration: underline; font-weight: 700; }
.spica-error-retry-button { padding: 4px 12px; border: 1px solid currentColor; border-radius: 4px; background: transparent; color: inherit; cursor: pointer; font-size: 12px; white-space: nowrap; }
.spica-error-close-button { margin-left: auto; border: none; background: none; cursor: pointer; font-size: 14px; color: inherit; }
`;

export interface ErrorBannerOptions {
  errorType: ErrorKind;
  message: string;
  /**
   * メッセージ内にインラインリンクを埋め込む場合に指定する（例: 同一企業重複時の「こちら」リンク）。
   * 指定時は`message`（前文）＋`linkText`（リンク）＋`afterText`（後文）を1つの段落として描画する。
   * XSS対策のためテキストは全てtextContentで設定し、hrefのみ`<a>`に付与する。
   */
  link?: { linkText: string; href: string; afterText?: string };
  onRetry?: () => void;
  onClose?: () => void;
}

/** エラー種別ごとのCSSクラス・アイコン（design-mockups準拠）。 */
function bannerVisualStyle(errorType: ErrorKind): { className: string; icon: string } {
  if (errorType === "BUSINESS") {
    return { className: "business", icon: "⚠️" };
  }
  return { className: "system", icon: "🛑" };
}

/**
 * エラーバナーを表示する。onRetryが指定された場合のみ手動再試行導線を表示する（自動リトライは行わない）。
 *
 * <p>画面中央のオーバーレイ（`role="dialog" aria-modal="true"`）として表示する。既存の中央モーダル
 * （`widgetPanelUi`/`calendarModalUi`）と同じアクセシビリティ属性を付与する。複数エラーの同時表示で
 * オーバーレイが積み重なるのを防ぐため、表示前に既存インスタンスを破棄して単一に保つ。
 */
export function showErrorMessageBanner(options: ErrorBannerOptions): void {
  // 単一インスタンス化: 既存のエラーバナーhostが残っていれば破棄してから作り直す
  // （全画面オーバーレイの積み重なり・背景クリック閉じの阻害を防ぐ）。
  document.querySelectorAll(`#${BANNER_HOST_ID}`).forEach((el) => el.remove());

  const { host, shadowRoot } = createShadowHost(BANNER_HOST_ID);
  applyStylesheet(shadowRoot, BANNER_STYLES);

  // ×ボタン・背景クリック・（将来的な）他導線で共有する閉じる処理。
  const close = (): void => {
    host.remove();
    options.onClose?.();
  };

  // 画面中央のオーバーレイ内にバナーを配置する（背景クリックで閉じる）。
  const overlay = createElement("div", { className: "spica-error-overlay" });

  const { className, icon } = bannerVisualStyle(options.errorType);
  const banner = createElement("div", {
    className: `spica-error-banner ${className}`,
    attrs: {
      role: "dialog",
      "aria-modal": "true",
      "aria-live": "polite",
      "aria-label": options.errorType === "BUSINESS" ? "エラー" : "システムエラー",
    },
  });

  const iconEl = createElement("span", { className: "spica-error-banner-icon", text: icon });
  banner.appendChild(iconEl);

  const message = createElement("p", { className: "spica-error-message" });
  if (options.link) {
    // 前文（message）＋インラインリンク（linkText）＋後文（afterText）を1段落として構築する。
    // テキストは全てcreateElement/textContent経由で挿入し、hrefのみ<a>に付与する（XSS安全）。
    message.appendChild(document.createTextNode(options.message));
    const link = createElement("a", {
      className: "spica-error-message-link",
      text: options.link.linkText,
      // 別ページ（サービスサイトのキャンセルページ等）へ誘導するため別タブで開く（不具合No372）。
      // rel="noopener noreferrer" はtabnabbing対策（別タブ遷移時のセキュリティベストプラクティス）。
      attrs: { href: options.link.href, target: "_blank", rel: "noopener noreferrer" },
    });
    message.appendChild(link);
    if (options.link.afterText) {
      message.appendChild(document.createTextNode(options.link.afterText));
    }
  } else {
    message.textContent = options.message;
  }
  banner.appendChild(message);

  if (options.onRetry) {
    const retryButton = createElement("button", { className: "spica-error-retry-button", text: "再試行" });
    retryButton.addEventListener("click", () => {
      host.remove();
      options.onRetry!();
    });
    banner.appendChild(retryButton);
  }

  const closeButton = createElement("button", { className: "spica-error-close-button", text: "×" });
  closeButton.addEventListener("click", close);
  banner.appendChild(closeButton);

  // オーバーレイ背景（バナー外側）クリックでも閉じる。
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) {
      close();
    }
  });

  overlay.appendChild(banner);
  shadowRoot.appendChild(overlay);
  document.body.appendChild(host);
}
